import Dinero from "dinero.js";
import startCase from "lodash/startCase";
import { DPReceivingAccount } from "Shared/types/Payments";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

function fullName(first: string, last: string) {
  return `${titleizeWord(first)} ${titleizeWord(last)}`;
}

const denominations = ["", "M"];
const abbreviateNumber = (cents: number) => {
  const dollars = cents / 100;
  const tier = Math.min(
    (Math.log10(dollars) / 6) | 0,
    denominations.length - 1
  );
  if (!tier) {
    return { abbreviatedNum: cents, suffix: "" };
  }
  const decimal = (dollars / Math.pow(10, tier * 6)).toFixed(1);
  return {
    abbreviatedNum: Math.floor(cents / Math.pow(10, tier * 6) / 100) * 100,
    suffix: `.${decimal.charAt(decimal.length - 1)}${denominations[tier]}`,
  };
};
function dollar(
  num?: number | null,
  format = "$0,0.00",
  { abbreviate }: { abbreviate?: boolean } = {}
): string {
  if (!num) {
    return "$0.00";
  }
  if (abbreviate) {
    const { abbreviatedNum, suffix } = abbreviateNumber(num);
    return Dinero({ amount: abbreviatedNum }).toFormat("$0,0") + suffix;
  }
  return Dinero({ amount: num }).toFormat(format);
}

type TestedDateFormats =
  | "MM/DD/YY"
  | "MMM DD, YYYY"
  | "MM/DD/YYYY"
  | "MM/DD/YY h:mm:ss a"
  | "MM/DD/YY h:mm:ss A"
  | "h:mm:ss a"
  | "YYYY-MM-DD";

const DEFAULT_DATE_FORMAT = "MM/DD/YY";
const DEFAULT_DATETIME_FORMAT = "MM/DD/YY h:mm:ss a";

export type DateOptions = {
  format?: TestedDateFormats;
  withTime?: boolean;
  utc?: boolean;
};

function date(
  isoTime: string,
  { format, withTime, utc }: DateOptions = {}
): string {
  const dayjsFn = utc ? dayjs.utc : dayjs;
  const formatStr = format ||
    (withTime ? DEFAULT_DATETIME_FORMAT : DEFAULT_DATE_FORMAT);
  return dayjsFn(isoTime, "YYYY-MM-DD[T]HH:mm:ss").format(formatStr);
}

function phone(digits: string | null) {
  if (!digits) {
    return "";
  }

  const cleaned = digits.replace(/\D/g, "");
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : digits;
}
function account(acct?: string | null) {
  return acct ? `**-${acct.slice(acct.length - 4, acct.length)}` : "";
}
function titleizeWord(word: string | null) {
  if (!word) {
    return "";
  }
  word = word.toLowerCase();
  const firstChar = word[0];
  return `${firstChar.toUpperCase()}${word.slice(1)}`;
}

function receivingAccount(receivingAccount: DPReceivingAccount) {
  if (!receivingAccount || !receivingAccount.receivingAccountType) {
    return "";
  }

  const accountNumber = receivingAccount.accountNumber;
  const accountName = receivingAccount.name;
  return receivingAccount.receivingAccountType === "DebitCard"
    ? `${accountName} ending in ${accountNumber}`
    : `${accountName} ${account(accountNumber)}`;
}

function file(path: string) {
  const fileName = path
    ? `${path.slice(path.lastIndexOf("/") + 1, path.length)}`
    : "";
  const extension = path
    ? `${path.slice(path.lastIndexOf(".") + 1, path.length)}`
    : "";
  return { fileName, extension };
}

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  fullName,
  dollar,
  date,
  phone,
  account,
  startCase,
  titleizeWord,
  receivingAccount,
  file,
};
